import { useEffect, useState } from 'react';

/**
 * Where someone registers the Bitbucket SSH key their pushes will use.
 *
 * The screen is built around one idea: saving a key proves nothing. A pasted
 * key may belong to the wrong Bitbucket account and a generated one is not on
 * Bitbucket at all until somebody carries the public half over. So the state
 * this dialog cares about is not "saved", it is "verified" -- and until it is
 * verified the screen says so and keeps the test button in front of you.
 *
 * Lives as a tab of the settings dialog, which owns the scrim, Escape and
 * the close button.
 */

interface GitKey {
  email: string;
  publicKey: string;
  fingerprint: string;
  comment: string;
  origin: 'pasted' | 'generated';
  allowFetch: boolean;
  verifiedAgainst?: string;
  createdAt: number;
  verifiedAt?: number;
  lastUsedAt?: number;
}

type Mode = 'paste' | 'generate';

async function api(path: string, init?: RequestInit) {
  const res = await fetch(path, {
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    ...init,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.message || body.error || `Request failed (${res.status})`);
  return body;
}

export function GitKeyPanel() {
  const [loading, setLoading] = useState(true);
  const [available, setAvailable] = useState(true);
  const [key, setKey] = useState<GitKey | null>(null);
  const [bitbucketUrl, setBitbucketUrl] = useState('https://bitbucket.org/account/settings/ssh-keys/');

  const [mode, setMode] = useState<Mode>('paste');
  const [pem, setPem] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [allowFetch, setAllowFetch] = useState(false);

  /** Only after generating: the half the person has to carry to Bitbucket. */
  const [freshPublicKey, setFreshPublicKey] = useState('');
  const [copied, setCopied] = useState(false);

  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    let cancelled = false;
    api('/api/git/key')
      .then((body) => {
        if (cancelled) return;
        setAvailable(body.available !== false);
        setKey(body.key ?? null);
        setAllowFetch(body.key?.allowFetch ?? false);
        if (body.bitbucketKeysUrl) setBitbucketUrl(body.bitbucketKeysUrl);
      })
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  function run(label: string, fn: () => Promise<void>) {
    setBusy(label);
    setError('');
    setNote('');
    fn()
      .catch((e: Error) => setError(e.message))
      .finally(() => setBusy(''));
  }

  const save = () =>
    run('save', async () => {
      const body = await api('/api/git/key', {
        method: 'POST',
        body: JSON.stringify(
          mode === 'generate'
            ? { mode, allowFetch }
            : { mode, privateKey: pem, passphrase: passphrase || undefined, allowFetch },
        ),
      });
      setKey(body.key);
      setPem('');
      setPassphrase('');
      // Only worth showing for a generated pair: for a pasted key Bitbucket
      // already has it, and putting a public key on screen would just invite
      // a second, pointless trip to the settings page.
      setFreshPublicKey(mode === 'generate' ? body.publicKey ?? '' : '');
      setCopied(false);
    });

  const test = () =>
    run('test', async () => {
      const body = await api('/api/git/key/test', { method: 'POST' });
      if (body.key) setKey(body.key);
      if (body.ok) setNote(body.message);
      else setError(body.message);
    });

  const toggleFetch = (next: boolean) =>
    run('fetch', async () => {
      const body = await api('/api/git/key', { method: 'PATCH', body: JSON.stringify({ allowFetch: next }) });
      setKey(body.key);
      setAllowFetch(body.key?.allowFetch ?? next);
    });

  const remove = () =>
    run('remove', async () => {
      await api('/api/git/key', { method: 'DELETE' });
      setKey(null);
      setFreshPublicKey('');
      setAllowFetch(false);
    });

  const copy = () => {
    void navigator.clipboard.writeText(freshPublicKey || key?.publicKey || '').then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <>
      <div className="share__head settings__head">
        <div>
          <div className="share__title" id="gitkey-title">
            Bitbucket erişimi
          </div>
          <div className="share__sub">
            Push yaptığında bu anahtar kullanılır ve commit senin adına görünür.
          </div>
        </div>
      </div>

      <div className="gitkey__body">
        {loading && <p className="share__empty">Yükleniyor…</p>}

        {!loading && !available && (
          <p className="gitkey__warn">
            Sunucuda <code>KEY_ENCRYPTION_SECRET</code> tanımlı değil, bu yüzden anahtar
            saklanamıyor. Yöneticiye söyle.
          </p>
        )}

        {!loading && available && key && (
          <>
            <div className="gitkey__card">
              <div className="gitkey__row">
                <strong>{key.comment || 'Anahtar'}</strong>
                <span className={`gitkey__badge ${key.verifiedAt ? 'gitkey__badge--ok' : ''}`}>
                  {key.verifiedAt ? 'doğrulandı' : 'doğrulanmadı'}
                </span>
              </div>
              <code className="gitkey__fp">{key.fingerprint}</code>
              <div className="gitkey__meta">
                {key.origin === 'generated' ? 'Cowork üretti' : 'Sen yapıştırdın'}
                {key.verifiedAgainst && ` · ${key.verifiedAgainst} erişimi doğrulandı`}
                {key.lastUsedAt && ` · son kullanım: ${new Date(key.lastUsedAt).toLocaleDateString()}`}
              </div>
            </div>

            {!key.verifiedAt && (
              <p className="gitkey__warn">
                Bu anahtarın Bitbucket'ta çalıştığı henüz doğrulanmadı.{' '}
                {key.origin === 'generated' && (
                  <>
                    Önce public key'i{' '}
                    <a href={bitbucketUrl} target="_blank" rel="noreferrer">
                      Bitbucket → SSH keys
                    </a>{' '}
                    altına ekle.
                  </>
                )}
              </p>
            )}

            {freshPublicKey && (
              <div className="gitkey__pub">
                <div className="gitkey__row">
                  <strong>Public key — bunu Bitbucket'a ekle</strong>
                  <button className="share__toggle" onClick={copy}>
                    {copied ? 'kopyalandı' : 'kopyala'}
                  </button>
                </div>
                <textarea readOnly rows={3} value={freshPublicKey} onFocus={(e) => e.currentTarget.select()} />
                <a href={bitbucketUrl} target="_blank" rel="noreferrer">
                  Bitbucket → Personal settings → SSH keys ↗
                </a>
              </div>
            )}

            <label className="gitkey__check">
              <input
                type="checkbox"
                checked={allowFetch}
                disabled={!!busy}
                onChange={(e) => toggleFetch(e.currentTarget.checked)}
              />
              <span>
                <strong>Sunucu repoları güncel tutmak için bu anahtarı kullanabilsin</strong>
                <em>
                  Yalnız okuma: fetch. Hiçbir şey push edilmez. Kimse giriş yapmamışken de
                  çalışır ve anahtar yalnız o sırada belleğe yüklenir.
                </em>
              </span>
            </label>

            <div className="gitkey__actions">
              <button className="gitkey__btn gitkey__btn--primary" onClick={test} disabled={!!busy}>
                {busy === 'test' ? 'Deneniyor…' : 'Bağlantıyı test et'}
              </button>
              <button className="gitkey__btn" onClick={remove} disabled={!!busy}>
                Anahtarı kaldır
              </button>
            </div>
          </>
        )}

        {!loading && available && !key && (
          <>
            <div className="gitkey__modes" role="tablist">
              <button
                role="tab"
                aria-selected={mode === 'paste'}
                className="gitkey__mode"
                onClick={() => setMode('paste')}
              >
                Anahtarım var
              </button>
              <button
                role="tab"
                aria-selected={mode === 'generate'}
                className="gitkey__mode"
                onClick={() => setMode('generate')}
              >
                Benim için oluştur
              </button>
            </div>

            {mode === 'paste' ? (
              <>
                <p className="gitkey__hint">
                  Özel anahtarını yapıştır — <code>~/.ssh/id_ed25519</code> gibi,{' '}
                  <code>-----BEGIN</code> ile başlayan dosyanın içeriği. <code>.pub</code> olan
                  değil.
                </p>
                <textarea
                  className="gitkey__pem"
                  rows={7}
                  spellCheck={false}
                  placeholder="-----BEGIN OPENSSH PRIVATE KEY-----"
                  value={pem}
                  onChange={(e) => setPem(e.currentTarget.value)}
                />
                <input
                  className="share__filter"
                  type="password"
                  placeholder="Passphrase (varsa)"
                  value={passphrase}
                  onChange={(e) => setPassphrase(e.currentTarget.value)}
                />
                <p className="gitkey__hint gitkey__hint--quiet">
                  Passphrase yalnız anahtarı açmak için kullanılır, saklanmaz. Anahtarın
                  sunucuda şifreli durur ve tarayıcıya bir daha hiç dönmez.
                </p>
              </>
            ) : (
              <p className="gitkey__hint">
                Cowork sana özel yeni bir ed25519 anahtarı üretir. Özel yarısı sunucudan hiç
                çıkmaz; sen yalnız public yarısını Bitbucket'a eklersin. Kendi bilgisayarındaki
                anahtarın işin içine hiç girmez, ve bu anahtarı iptal etmek başka hiçbir
                erişimini etkilemez.
              </p>
            )}

            <label className="gitkey__check">
              <input
                type="checkbox"
                checked={allowFetch}
                onChange={(e) => setAllowFetch(e.currentTarget.checked)}
              />
              <span>
                <strong>Sunucu repoları güncel tutmak için bu anahtarı kullanabilsin</strong>
                <em>Yalnız okuma: fetch. Hiçbir şey push edilmez. Sonradan kapatabilirsin.</em>
              </span>
            </label>

            <div className="gitkey__actions">
              <button
                className="gitkey__btn gitkey__btn--primary"
                onClick={save}
                disabled={!!busy || (mode === 'paste' && pem.trim().length === 0)}
              >
                {busy === 'save'
                  ? 'Kaydediliyor…'
                  : mode === 'generate'
                    ? 'Anahtar oluştur'
                    : 'Anahtarı kaydet'}
              </button>
            </div>
          </>
        )}

        {error && <p className="gitkey__error">{error}</p>}
        {note && <p className="gitkey__note">{note}</p>}
      </div>

      <div className="share__foot">
        Anahtarın sunucuda şifrelenerek saklanır ve yalnız senin onayladığın bir push sırasında,
        o işlemin süresi boyunca kullanılır.
      </div>
    </>
  );
}
